import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Lock,
  EyeOff,
  ShieldCheck,
  Sparkles,
  Rocket,
  Loader2,
  AlertCircle,
  KeyRound,
} from "lucide-react";

import { DravianLogo } from "@/components/dravian/logo";
import { FooterStrip } from "@/components/dravian/footer-strip";
import {
  llmCredentials,
  onboarding as onboardingApi,
  type IndustryId,
  type LlmProviderName,
} from "@/lib/api";

export const Route = createFileRoute("/onboarding")({
  head: () => ({
    meta: [
      { title: "Setup — Dravian Horizon" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "/onboarding" }],
  }),
  component: OnboardingPage,
});

const STEPS = [
  "How Horizon handles your data",
  "Connect your AI provider",
  "Tell us about your organization",
  "Optional: help us improve",
] as const;

const INDUSTRIES: { id: IndustryId; label: string }[] = [
  { id: "government", label: "Government / Public sector" },
  { id: "healthcare", label: "Healthcare" },
  { id: "finance", label: "Finance / Banking" },
  { id: "defense", label: "Defense / National security" },
  { id: "ngo", label: "NGO / Humanitarian" },
  { id: "energy", label: "Energy / Utilities" },
  { id: "telecom", label: "Telecom" },
  { id: "other", label: "Other" },
];

const PROVIDERS: { id: LlmProviderName; label: string; hint: string }[] = [
  { id: "anthropic", label: "Anthropic", hint: "Claude (recommended)" },
  { id: "openai", label: "OpenAI", hint: "GPT-4o, GPT-4 Turbo" },
  { id: "azure_openai", label: "Azure OpenAI", hint: "Enterprise tenancy" },
  { id: "bedrock", label: "AWS Bedrock", hint: "Claude / Llama via AWS" },
];

type TestResult =
  | { state: "idle" }
  | { state: "testing" }
  | { state: "ok"; model?: string }
  | { state: "fail"; error: string };

function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(0);

  // Step 1
  const [acknowledged, setAcknowledged] = useState(false);

  // Step 2
  const [provider, setProvider] = useState<LlmProviderName>("anthropic");
  const [apiKey, setApiKey] = useState("");
  const [testResult, setTestResult] = useState<TestResult>({ state: "idle" });
  const [llmSaved, setLlmSaved] = useState(false);

  // Step 3
  const [industry, setIndustry] = useState<IndustryId>("government");
  const [useCase, setUseCase] = useState("");

  // Step 4
  const [shareMetadata, setShareMetadata] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  const stepProgressPct = useMemo(
    () => ((step + 1) / STEPS.length) * 100,
    [step],
  );

  const stepValid = (() => {
    if (step === 0) return acknowledged;
    if (step === 1) return llmSaved;
    if (step === 2) return useCase.trim().length > 0 && industry;
    if (step === 3) return true;
    return false;
  })();

  async function testKey() {
    if (!apiKey) return;
    setTestResult({ state: "testing" });
    try {
      const r = await llmCredentials.test({ provider, api_key: apiKey });
      if (r.ok) {
        setTestResult({ state: "ok", model: r.model });
      } else {
        setTestResult({ state: "fail", error: r.error ?? "Unknown error" });
      }
    } catch (e) {
      setTestResult({ state: "fail", error: String(e) });
    }
  }

  async function saveKey() {
    if (testResult.state !== "ok") return;
    setSubmitError(null);
    try {
      await llmCredentials.upsert({
        provider,
        api_key: apiKey,
        activate: true,
      });
      setLlmSaved(true);
    } catch (e) {
      setSubmitError(`Saving the key failed: ${e}`);
    }
  }

  async function finish() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onboardingApi.complete({
        industry,
        use_case: useCase.trim(),
        share_metadata_with_dravian: shareMetadata,
      });
      // Invalidate the gate so the RouteGuard lets us out of /onboarding.
      await queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
      setFinished(true);
    } catch (e) {
      setSubmitError(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (finished) {
    return <FinishedScreen onContinue={() => navigate({ to: "/hq" })} />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="flex items-center justify-between px-6 py-5 border-b border-border">
        <DravianLogo />
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Setup · {step + 1} of {STEPS.length}
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-[760px] px-6 py-10">
        {/* Progress bar */}
        <div className="h-0.5 w-full bg-border mb-8">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${stepProgressPct}%` }}
          />
        </div>

        <div className="label-eyebrow mb-1">Setup · {step + 1} of {STEPS.length}</div>
        <h1 className="text-[22px] font-semibold tracking-tight">{STEPS[step]}</h1>

        <div className="panel p-6 mt-6 min-h-[380px]">
          {step === 0 && (
            <DataHandlingStep
              acknowledged={acknowledged}
              onToggle={setAcknowledged}
            />
          )}
          {step === 1 && (
            <LlmStep
              provider={provider}
              setProvider={(p) => {
                setProvider(p);
                setTestResult({ state: "idle" });
                setLlmSaved(false);
              }}
              apiKey={apiKey}
              setApiKey={(v) => {
                setApiKey(v);
                setTestResult({ state: "idle" });
                setLlmSaved(false);
              }}
              testResult={testResult}
              onTest={testKey}
              onSave={saveKey}
              llmSaved={llmSaved}
            />
          )}
          {step === 2 && (
            <OrgStep
              industry={industry}
              setIndustry={setIndustry}
              useCase={useCase}
              setUseCase={setUseCase}
            />
          )}
          {step === 3 && (
            <OptInStep
              shareMetadata={shareMetadata}
              setShareMetadata={setShareMetadata}
              industry={industry}
              useCase={useCase}
            />
          )}
        </div>

        {submitError && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>{submitError}</div>
          </div>
        )}

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || submitting}
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              disabled={!stepValid}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-[13px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {step === 0 ? "I understand · Continue" : "Continue"}
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={finish}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-[13px] font-medium glow-primary disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
              Finish setup
            </button>
          )}
        </div>
      </main>

      <FooterStrip />
    </div>
  );
}

// ---------- Step components ----------

function DataHandlingStep({
  acknowledged,
  onToggle,
}: {
  acknowledged: boolean;
  onToggle: (v: boolean) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto h-14 w-14 rounded-full border border-primary/30 bg-primary/10 grid place-items-center mb-4 glow-primary">
          <ShieldCheck className="h-6 w-6 text-primary" />
        </div>
        <h2 className="text-[20px] font-semibold tracking-tight">
          Your data stays on your machine.
        </h2>
        <p className="text-[13px] text-muted-foreground mt-2 max-w-[520px] mx-auto">
          We've already validated who you are and which organization you belong
          to. From here on, <strong className="text-foreground">Horizon doesn't collect anything else</strong>. Entities,
          signals, scenarios, chats, AI traffic — all of it lives on this
          deployment. No silent uploads. No analytics SDK.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Pillar
          icon={Lock}
          title="Local-only data"
          desc="Entities, signals, chats, AI traffic, audit log — all in your Postgres + Neo4j. Never our servers."
        />
        <Pillar
          icon={EyeOff}
          title="No telemetry by default"
          desc="No usage pings. No crash reports. No anonymized events. You'll be asked once if you want to share anything — and the answer can be no."
        />
        <Pillar
          icon={ShieldCheck}
          title="License heartbeat only"
          desc="A daily call to Dravian confirms your seat is still active. That's the entire wire payload."
        />
      </div>

      <label className="flex items-start gap-3 rounded-md border border-border bg-surface-elevated/40 p-4 cursor-pointer hover:border-primary/30 transition">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => onToggle(e.target.checked)}
          className="accent-primary h-4 w-4 mt-0.5"
        />
        <div className="text-[12px] text-muted-foreground">
          I understand that everything I do in Horizon — entities, signals,
          chats, documents, AI traffic — is stored on this deployment. Dravian
          only sees that my seat is still active.
        </div>
      </label>
    </div>
  );
}

function LlmStep({
  provider,
  setProvider,
  apiKey,
  setApiKey,
  testResult,
  onTest,
  onSave,
  llmSaved,
}: {
  provider: LlmProviderName;
  setProvider: (p: LlmProviderName) => void;
  apiKey: string;
  setApiKey: (v: string) => void;
  testResult: TestResult;
  onTest: () => void;
  onSave: () => void;
  llmSaved: boolean;
}) {
  return (
    <div className="space-y-5">
      <p className="text-[13px] text-muted-foreground">
        Horizon uses your AI provider for chat, entity extraction, and
        scenario generation. Your key stays encrypted on this deployment.
      </p>

      <div>
        <div className="label-eyebrow mb-1.5">Provider</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {PROVIDERS.map((p) => {
            const active = provider === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setProvider(p.id)}
                className={`text-left rounded-md border px-3 py-2.5 transition ${
                  active
                    ? "border-primary/50 bg-primary/10"
                    : "border-border hover:border-primary/30 bg-surface-elevated/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[13px] font-semibold">{p.label}</div>
                  {active && <Check className="h-3.5 w-3.5 text-primary" />}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {p.hint}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="label-eyebrow mb-1.5">API key</div>
        <div className="flex items-center gap-2 rounded-md border border-border bg-surface-elevated px-3 py-2.5 focus-within:border-primary/50 transition">
          <KeyRound className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="sk-… / ak-… / ya29-…"
            className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/60"
          />
        </div>
        <div className="text-[11px] text-muted-foreground mt-1.5">
          The key is encrypted at rest using a master key on this deployment.
          Anyone with this Horizon instance can use it — but it never leaves.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onTest}
          disabled={!apiKey || testResult.state === "testing"}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-elevated px-3 py-1.5 text-[12px] hover:border-primary/40 disabled:opacity-40"
        >
          {testResult.state === "testing" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          Test connection
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={testResult.state !== "ok" || llmSaved}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Check className="h-3.5 w-3.5" />
          {llmSaved ? "Saved" : "Save and activate"}
        </button>
        {testResult.state === "ok" && (
          <span className="text-[11px] text-emerald-400">
            ✓ Connected{testResult.model ? ` · ${testResult.model}` : ""}
          </span>
        )}
        {testResult.state === "fail" && (
          <span className="text-[11px] text-destructive">{testResult.error}</span>
        )}
      </div>
    </div>
  );
}

function OrgStep({
  industry,
  setIndustry,
  useCase,
  setUseCase,
}: {
  industry: IndustryId;
  setIndustry: (i: IndustryId) => void;
  useCase: string;
  setUseCase: (v: string) => void;
}) {
  return (
    <div className="space-y-5">
      <p className="text-[13px] text-muted-foreground">
        Quick context so the workspace, ontology, and chat suggestions match
        what you actually do. <strong className="text-foreground">This stays on your machine</strong> unless you opt in on
        the next step.
      </p>

      <div>
        <div className="label-eyebrow mb-1.5">Industry</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {INDUSTRIES.map((i) => {
            const active = industry === i.id;
            return (
              <button
                key={i.id}
                type="button"
                onClick={() => setIndustry(i.id)}
                className={`text-left rounded-md border px-3 py-2.5 text-[13px] transition ${
                  active
                    ? "border-primary/50 bg-primary/10"
                    : "border-border hover:border-primary/30 bg-surface-elevated/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium">{i.label}</div>
                  {active && <Check className="h-3.5 w-3.5 text-primary" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="label-eyebrow mb-1.5">What do you want to analyze first?</div>
        <textarea
          value={useCase}
          onChange={(e) => setUseCase(e.target.value.slice(0, 280))}
          rows={3}
          placeholder="E.g. Adverse-event tracking for medical devices in clinical pilots."
          className="w-full rounded-md border border-border bg-surface-elevated px-3 py-2.5 text-[13px] outline-none focus:border-primary/50 transition placeholder:text-muted-foreground/60 resize-none"
        />
        <div className="text-[11px] text-muted-foreground mt-1 text-right">
          {useCase.length} / 280
        </div>
      </div>
    </div>
  );
}

function OptInStep({
  shareMetadata,
  setShareMetadata,
  industry,
  useCase,
}: {
  shareMetadata: boolean;
  setShareMetadata: (v: boolean) => void;
  industry: IndustryId;
  useCase: string;
}) {
  const industryLabel = INDUSTRIES.find((i) => i.id === industry)?.label ?? industry;
  return (
    <div className="space-y-5">
      <p className="text-[13px] text-muted-foreground">
        Optional. If you'd like to help Dravian build a better product, you can
        share two pieces of metadata. Nothing else changes.
      </p>

      <label className="flex items-start gap-3 rounded-md border border-border bg-surface-elevated/40 p-4 cursor-pointer hover:border-primary/30 transition">
        <input
          type="checkbox"
          checked={shareMetadata}
          onChange={(e) => setShareMetadata(e.target.checked)}
          className="accent-primary h-4 w-4 mt-0.5"
        />
        <div>
          <div className="text-[13px] font-semibold">
            Share my industry and use case with Dravian
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Sent alongside the daily license heartbeat. Helps Dravian
            prioritize features for organizations like yours. You can flip
            this off at any time from Settings.
          </div>
        </div>
      </label>

      <div className="rounded-md border border-border bg-surface-elevated/30 p-4 text-[12px] space-y-2">
        <div className="label-eyebrow">
          {shareMetadata ? "What you'll share" : "What stays only on this machine"}
        </div>
        <ul className="space-y-1.5 text-muted-foreground">
          <li className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-foreground">industry</span>
            <span>→ {industryLabel}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-foreground">use_case</span>
            <span>→ {useCase || "(empty)"}</span>
          </li>
        </ul>
        <div className="pt-2 mt-2 border-t border-border text-[11px] text-muted-foreground">
          Workspaces, entities, signals, chats, documents, AI traffic →
          <strong className="text-foreground"> always local</strong>, regardless of this choice.
        </div>
      </div>
    </div>
  );
}

function Pillar({
  icon: Icon,
  title,
  desc,
}: {
  icon: typeof Lock;
  title: string;
  desc: string;
}) {
  return (
    <div className="rounded-md border border-border bg-surface-elevated/40 p-4">
      <Icon className="h-4 w-4 text-primary mb-2" />
      <div className="text-[12px] font-semibold tracking-tight uppercase">{title}</div>
      <div className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{desc}</div>
    </div>
  );
}

function FinishedScreen({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="flex items-center justify-between px-6 py-5 border-b border-border">
        <DravianLogo />
      </header>
      <main className="flex-1 grid place-items-center px-6">
        <div className="text-center max-w-[480px]">
          <div className="mx-auto h-14 w-14 rounded-full border border-primary/30 bg-primary/10 grid place-items-center mb-6 glow-primary">
            <Check className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-[24px] font-semibold tracking-tight">You're all set.</h1>
          <p className="text-[13px] text-muted-foreground mt-2">
            Horizon is configured for this deployment. Your team can now sign in
            and start working — anything they do stays on this infrastructure.
          </p>
          <button
            onClick={onContinue}
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-[13px] font-medium glow-primary"
          >
            Go to HQ <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </main>
      <FooterStrip />
    </div>
  );
}
